import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { config } from "../lib/config";
import { GoogleG } from "../components/SignInButton";

const REDEEM_ERRORS: Record<string, string> = {
  invalid_token: "That invitation code isn't valid.",
  token_used: "That invitation has already been redeemed.",
  token_expired: "That invitation has expired. Ask for a new one.",
  email_mismatch: "This code was issued for a different email address.",
  too_many_attempts: "Too many attempts. Please wait and try again later.",
  weak_password: "Choose a password with at least 10 characters.",
};

export function RedeemPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") ?? "");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    user,
    ready,
    googlePending,
    redeemGoogleError,
    beginGoogleRedeem,
    cancelGoogleRedeem,
    renderGoogleButton,
  } = useAuth();
  const googleHostRef = useRef<HTMLDivElement>(null);
  const googleEnabled = Boolean(config.googleClientId);
  const trimmedToken = token.trim().toUpperCase();

  // Google redemption (auth.tsx) mints a session directly rather than
  // returning through this component, so land on the predictions page
  // ourselves once that session appears — same as the password pathway's
  // VerifyEmail step does after it adopts a session token.
  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  // Point the shared Google button at this invitation code so a credential
  // redeems it instead of attempting a normal login (auth.tsx). On success
  // auth.tsx mints a session directly, which flips App into the signed-in
  // shell — this page never needs its own "done" state for that path.
  useEffect(() => {
    if (!googleEnabled || !trimmedToken) return;
    beginGoogleRedeem(trimmedToken);
    return () => cancelGoogleRedeem();
  }, [googleEnabled, trimmedToken, beginGoogleRedeem, cancelGoogleRedeem]);

  useEffect(() => {
    if (!ready || !googleEnabled) return;
    const host = googleHostRef.current;
    if (!host) return;
    if (!trimmedToken) {
      // No invitation code yet: don't leave a stale button wired to nothing
      // (a click would fall through to a normal, doomed-to-fail login).
      host.innerHTML = "";
      return;
    }
    renderGoogleButton(host);
  }, [ready, googleEnabled, trimmedToken, renderGoogleButton]);

  const googleErrorMessage =
    redeemGoogleError && REDEEM_ERRORS[redeemGoogleError]
      ? REDEEM_ERRORS[redeemGoogleError]
      : redeemGoogleError
        ? "Couldn't redeem the invitation. Please try again."
        : null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.redeemWithPassword(
        token.trim().toUpperCase(),
        email.trim().toLowerCase(),
        password,
      );
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && REDEEM_ERRORS[err.code]
          ? REDEEM_ERRORS[err.code]
          : "Couldn't redeem the invitation. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <section className="page auth-page">
        <h1>Check your email</h1>
        <p>
          We sent a verification code to <strong>{email}</strong>. Enter it on
          the <Link to="/verify-email">verification page</Link> to finish
          creating your account.
        </p>
      </section>
    );
  }

  return (
    <section className="page auth-page">
      <h1>Redeem your invitation</h1>
      <p className="muted">
        Enter the code from your invitation email and choose a password, or
        continue with Google.
      </p>
      {googleEnabled ? (
        <>
          {googleErrorMessage ? (
            <p className="auth-error" role="alert">
              {googleErrorMessage}
            </p>
          ) : null}
          <div className="signin-provider">
            <div className="signin-provider-pill" aria-hidden>
              <GoogleG />
              <span>{googlePending ? "Redeeming…" : "Continue with Google"}</span>
            </div>
            <div
              ref={googleHostRef}
              className="signin-google-host"
              aria-label="Continue with Google"
            />
          </div>
          <div className="signin-divider">
            <span>or use a password</span>
          </div>
        </>
      ) : null}
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <label className="auth-label" htmlFor="redeem-email">
          Email
          <input
            id="redeem-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="auth-label" htmlFor="redeem-token">
          Invitation code
          <input
            id="redeem-token"
            inputMode="text"
            autoCapitalize="characters"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </label>
        <label className="auth-label" htmlFor="redeem-password">
          Choose a password
          <input
            id="redeem-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "Redeeming…" : "Redeem invitation"}
        </button>
      </form>
    </section>
  );
}
